import { useEffect, useMemo } from 'react';
import { useTable, useSortBy, usePagination, useFlexLayout } from 'react-table';
import { Query } from '../../hooks/use-pagination';
import { Icons } from '../icons/icons';
import { Pagination } from './pagination';

type Column = {
  accessor: string;
  Header: string;
  sort?: boolean;
  Cell?: any;
  width?: number;
};

interface Props {
  columns: Column[];
  onUpdate?(item: any): void;
  onDelete?(item: any): void;
  onChange?: (query: Query) => void;
  data?: any;
  isLoading?: boolean;
}

export function PaginatedTable(props: Props): JSX.Element {
  const columns = useMemo(() => props.columns?.map(column => getColumnWidth(column, props.data?.content || [])), [props.columns]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    flatHeaders,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      manualPagination: true,
      data: props.data?.content || [],
      pageCount: props.data?.totalPages,
      manualSortBy: true,
      initialState: {
        pageSize: props.data?.size || 10,
        pageIndex: props.data?.number || 0
      }
    },
    useSortBy,
    usePagination,
    useFlexLayout
  );

  useEffect(() => {
    const sort = flatHeaders.map(
      header => header.isSorted ? `${header.id},${header.isSortedDesc ? 'desc' : 'asc'}` : ''
    ).filter(item => item).join('&sort=');
    props.onChange?.({ page: pageIndex, size: pageSize, sort });
  }, [pageCount, pageSize, pageIndex, flatHeaders.map(header => header.isSortedDesc).toString()]);

  useEffect(() => {
    if (typeof props.data?.number === 'number' && pageIndex !== props.data?.number) {
      gotoPage(props.data.number as number);
    }
  }, [props.data]);

  return (
    <>
      <div className="new-table-wrapper">
        <div {...getTableProps()} className="new-table" style={{ maxWidth: '100%', overflowX: 'auto' }}>
          <div>
            {headerGroups.map((headerGroup) => (
              <div {...headerGroup.getHeaderGroupProps()} className="new-thead">
                {headerGroup.headers.map((column: any) => {
                  const props = column.sort
                    ? column.getHeaderProps(column.getSortByToggleProps())
                    : column.getHeaderProps();
                  return (
                    <div {...props} className={`header-cell new-th ${column.id === 'locked' ? 'row-status' : column.id === 'actions' ? 'new-th-action' : ''}`}>
                      {column.render('Header')}
                      {column.sort && (
                        <span className="sort-arrows">
                          {!column.isSorted ? <Icons.SortArrows /> : column.isSortedDesc ? <Icons.SortArrowUp /> : <Icons.SortArrowDown />}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="new-tbody" {...getTableBodyProps()}>
            {page?.map((row) => {
              prepareRow(row);
              return (
                <div {...row.getRowProps()} className="new-row">
                  {row.cells.map((cell) => {
                    if (cell.column.id === 'actions') {
                      return cell.render('Cell', { key: cell.column.id });
                    }
                    return (
                      <div {...cell.getCellProps()} className={`body-cell new-td ${cell.column.id === 'locked' ? 'row-status' : ''}`}>
                        {cell.render('Cell')}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        {props.isLoading && (
          <div className='text-center no-data-container'>
            <div><Icons.Data /></div>
            <p className="no-data-table">Loading...</p>
          </div>
        )}
        {!props.isLoading && props.data?.empty === true && (
          <div className='text-center no-data-container'>
            <div><Icons.Data /></div>
            <p className="no-data-table">No data available in table.</p>
          </div>
        )}
      </div>
      <Pagination {
        ...{ canPreviousPage, canNextPage, pageCount, previousPage, pageIndex, pageSize, rowsLength: props.data?.totalElements, setPageSize, gotoPage, nextPage }
      }/>
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getColumnWidth = (column: Column, rows: any): Column => {
  const minWidth = 150;
  const maxWidth = 400;
  const space = 10;
  const cellLength = Math.max(
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    ...(rows as any[]).map((row: any) => (`${row[column.accessor]}` || '').length),
    column.Header.length,
  );
  const columnWidth = Math.min(maxWidth, cellLength * space);
  return {
    ...column,
    width: columnWidth > minWidth ? columnWidth : minWidth
  };
};
